import React, { useState } from 'react';
import { ArrowUp, Flag, Loader2, Sparkles, X } from 'lucide-react';
import { SpeakerSegment } from '../../types';
import { t } from '../../lib/i18n';
import { displaySpeaker } from './speakerLabels';
import { ReportAiDialog, type AiReportTarget } from '../aiReport/ReportAiDialog';

/**
 * Ana süreç bağlamı baştan kırpıyor (`slice(0, MAX_CONTEXT_LENGTH)`), yani
 * uzun bir toplantıda EN ESKİ kısım korunurdu. Toplantı sürerken önemli olan
 * son konuşulanlar olduğu için kırpmayı burada, sondan yapıyoruz.
 */
const MAX_CONTEXT_CHARS = 18_000;

const catchMeUpPrompt = () =>
  t(
    'Toplantıda şu ana kadar konuşulanları kısaca özetle ve varsa kararları listele.',
    'Briefly summarize what has been discussed in the meeting so far and list any decisions.'
  );

interface RecordingAskBarProps {
  segments: SpeakerSegment[];
  isDarkMode: boolean;
}

/**
 * Kayıt sürerken toplantı hakkında soru sorma çubuğu. Bağlam olarak o ana
 * kadarki canlı transkript gidiyor — "geç kaldım, ne konuşuldu?" durumunun
 * karşılığı.
 */
export const RecordingAskBar: React.FC<RecordingAskBarProps> = ({ segments, isDarkMode }) => {
  const [question, setQuestion] = useState('');
  const [answer, setAnswer] = useState<string | null>(null);
  /** Yanıtın sorusu: hata değil gerçek bir yanıtsa bildirilebilir. */
  const [answered, setAnswered] = useState<string | null>(null);
  const [isAsking, setIsAsking] = useState(false);
  const [reportTarget, setReportTarget] = useState<AiReportTarget | null>(null);

  const hasTranscript = segments.length > 0;

  const ask = async (prompt: string) => {
    const trimmed = prompt.trim();
    if (!trimmed || isAsking) return;

    setIsAsking(true);
    setAnswer(null);
    setAnswered(null);
    try {
      const context = segments
        .map((segment) => `${displaySpeaker(segment.speaker)}: ${segment.text}`)
        .join('\n')
        .slice(-MAX_CONTEXT_CHARS);

      // Başlığı sunucu ekliyor; canlı transkript yalnızca kayıt sürerken kabul ediliyor.
      const reply = await window.zabit.chatWithAI([{ role: 'user', text: trimmed }], context);
      setAnswer(reply || t('(boş yanıt döndü)', '(empty response)'));
      if (reply) setAnswered(trimmed);
    } catch (err) {
      setAnswer(`⚠️ ${(err as Error).message}`);
    } finally {
      setIsAsking(false);
      setQuestion('');
    }
  };

  return (
    /**
     * Sayfaya yapışık soru çubuğu. Üstteki ince ayırıcı, çubuğun akan
     * transkriptin devamı değil ayrı bir bölge olduğunu gösteriyor —
     * ayırıcı olmadan boşlukta yüzüyor gibi duruyordu.
     */
    <div
      className={`shrink-0 px-6 pt-3 pb-4 border-t ${
        isDarkMode ? 'border-[#333336]' : 'border-neutral-200'
      }`}
    >
      <div className="max-w-3xl mx-auto w-full space-y-2.5">
        {answer && (
          <div
            className={`relative rounded-xl border p-3.5 ${answered !== null ? 'pr-16' : 'pr-9'} text-[13px] leading-relaxed whitespace-pre-wrap ${
              isDarkMode
                ? 'bg-[#27272B] border-[#3B3B3F] text-neutral-200'
                : 'bg-neutral-50 border-neutral-200 text-neutral-800'
            }`}
          >
            {answer}
            {answered !== null && (
              <button
                onClick={() => setReportTarget({ surface: 'live_ask', content: answer, prompt: answered })}
                title={t('Yanıtı bildir', 'Report response')}
                aria-label={t('Yanıtı bildir', 'Report response')}
                className="absolute top-2.5 right-9 p-1 rounded text-neutral-500 hover:text-rose-400 transition-colors"
              >
                <Flag className="w-3.5 h-3.5" />
              </button>
            )}
            <button
              onClick={() => setAnswer(null)}
              aria-label={t('Yanıtı kapat', 'Close answer')}
              className="absolute top-2.5 right-2.5 p-1 rounded text-neutral-500 hover:text-[#F0EEE6] transition-colors"
            >
              <X className="w-3.5 h-3.5" />
            </button>
          </div>
        )}

        <form
          onSubmit={(e) => {
            e.preventDefault();
            void ask(question);
          }}
          className={`flex items-center gap-2 rounded-xl border px-3.5 py-2 ${
            isDarkMode ? 'bg-[#19191C] border-[#3B3B3F]' : 'bg-white border-neutral-200'
          }`}
        >
          <input
            id="recording-ask-input"
            value={question}
            onChange={(e) => setQuestion(e.target.value)}
            disabled={!hasTranscript || isAsking}
            placeholder={
              hasTranscript
                ? t('Toplantı hakkında sor…', 'Ask about the meeting…')
                : t('Transkript oluştuğunda soru sorabilirsiniz', 'You can ask questions once there’s a transcript')
            }
            className="flex-1 bg-transparent outline-none text-[13.5px] placeholder:text-neutral-500 disabled:cursor-not-allowed"
          />

          <button
            type="button"
            onClick={() => void ask(catchMeUpPrompt())}
            disabled={!hasTranscript || isAsking}
            title={t('Şu ana kadar ne konuşulduğunu özetle', 'Summarize what has been said so far')}
            className="flex items-center gap-1.5 text-[12.5px] font-medium text-neutral-400 hover:text-[#46CBE2] px-2 py-1 rounded-lg transition-colors disabled:opacity-40 disabled:hover:text-neutral-400"
          >
            <Sparkles className="w-3.5 h-3.5" />
            {t('Beni yakala', 'Catch me up')}
          </button>

          <button
            type="submit"
            disabled={!question.trim() || isAsking}
            aria-label={t('Sor', 'Ask')}
            className="p-1.5 rounded-lg bg-[#1DA4BA] hover:bg-[#3AC8E0] text-[#F0EEE6] transition-colors disabled:opacity-40"
          >
            {isAsking ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <ArrowUp className="w-3.5 h-3.5" />}
          </button>
        </form>
      </div>
      <ReportAiDialog target={reportTarget} onClose={() => setReportTarget(null)} />
    </div>
  );
};
